import { AxiosError } from "axios";
import React from "react";
import toast, { type Toast as ToastInstance } from "react-hot-toast";
import { defineMessages, type MessageDescriptor } from "react-intl";

import { Toast } from "./components/ui";
import { httpErrorMessages, blacklistedErrorMessages } from "./utils/errors";

export type ToastText = string | MessageDescriptor;
export type ToastType = "success" | "error" | "info";

interface ToastOptionsProps {
  action?(): void;
  actionLink?: string;
  actionLabel?: ToastText;
  duration?: number;
  summary?: string;
}

const DEFAULT_DURATION = 4000;

const createToast = (
  type: ToastType,
  message: ToastText,
  opts?: ToastOptionsProps,
) => {
  const duration = opts?.duration || DEFAULT_DURATION;

  return toast.custom(
    (t: ToastInstance) => (
      <Toast t={t} message={message} type={type} {...opts} />
    ),
    {
      duration,
      id: typeof message === "string" ? message : message.id, // prevent duplicate toasts with the same text
    },
  );
};

function info(message: ToastText, opts?: ToastOptionsProps) {
  return createToast("info", message, opts);
}

function success(message: ToastText, opts?: ToastOptionsProps) {
  return createToast("success", message, opts);
}

function error(message: ToastText, opts?: ToastOptionsProps) {
  return createToast("error", message, opts);
}

const messages = defineMessages({
  unexpectedMessage: {
    id: "alert.unexpected.message",
    defaultMessage: "An unexpected error occurred.",
  },
  serverDown: {
    id: "alert.server_down.message",
    defaultMessage: "The server is down",
  },
});

function showAlertForError(networkError: AxiosError<any>) {
  if (networkError?.response) {
    const { data, status, statusText } = networkError.response;

    const fallbackMsg = httpErrorMessages.find(
      (httpError) => httpError.code === status,
    )?.description;

    if (status === 502) {
      return error(messages.serverDown);
    }

    if (status === 404 || status === 410) {
      // Skip these errors as they are reflected in the UI
      return null;
    }

    // If it's a 429 (rate limit), let's use our fallback message.
    if (status === 429 && fallbackMsg) {
      return error(fallbackMsg);
    }

    let message: string | undefined = statusText;

    if (data?.error || data?.error_message) {
      message = data.error_message || data.error;

      const blacklisted = blacklistedErrorMessages.find(
        (httpError) => httpError.description === message,
      );

      if (status === 403 && blacklisted) {
        return null;
      }
    }

    if (!message) {
      message = fallbackMsg;
    }

    if (message) {
      return error(message);
    }
  } else {
    console.error(networkError);
    return error(messages.unexpectedMessage);
  }
}

export default {
  info,
  success,
  error,
  showAlertForError,
};
